import { createStore} from 'vuex';
import isArray from "lodash/isArray";
import mapValues from "lodash/mapValues";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import has from "lodash/has";
import isUndefined from "lodash/isUndefined";
import omitBy from "lodash/omitBy";
import debounce from "lodash/debounce";
import makerjs, { IModel } from "makerjs";

import Client from "@/utilities/Client";

import cutoutState from './modules/cutoutState';
import drillHoleState from './modules/drillHoleState';
import fonts from './modules/fonts';
import ui from './modules/uiState';
import config from './modules/config';
import validation from './modules/validation';
import shapeState from './modules/shapeState';

import { CutoutTypes } from '@/Models/CutoutTypes';
import AbstractShape from "@/Models/Shapes/AbstractShape";

import Length from "../../../source/js/shared/models/Geometry/Length";
import ShapeFactory from "../../../source/js/shared/models/ShapeFactory";
import NullShape from "../../../source/js/shared/models/Geometry/NullShape";
import {convertUnits, countDecimals, formatToLocaleString} from "@/functions/numbers";
import ShapeConfigurationFactory from '@/Models/ShapeConfigurations/ShapeConfigurationFactory';
import { renderMaxLength } from "@/functions/lengths";
import { sprintf } from 'sprintf-js';
import edgeFinishStore from './modules/edgeFinishStore';
import PricingGroup from '@/Models/Pricing/PricingGroup';
import fileUploadState from './modules/fileUploadState';
import { addCaptionBackgrounds, addStyleToSVG, dottedGuideLines, getTexturedSVG, reorderSVG } from '@/utilities/SVGPreview';
import { ShapeModelDecorator, CustomDrillHoleDecorator, CutoutModelDecorator, DimensionLineDecorator, GeneratedDrillHoleDecorator, InterfaceModelDecorator, currentCutoutModelDecorator, CurrentCustomDrillHoleDecorator } from '@/utilities/decorators/PreviewModelDecorator';
import { FilletModelDecorator, ShapeModelCombineDecorator } from '@/utilities/decorators/ShapeModelDecorator';
import { GuideLineDecorator } from '@/utilities/decorators/DimensionLineDecorator';
import DrillHoleOffsetLineDecorator, { CurrentGeneratedDrillHoleDecorator } from '@/utilities/decorators/DrillHoleDecorator';
import { StepTypes } from '@/types/step';
import PresetFactory from '@/presets/PresetFactory';
import { AbstractPreset } from '@/presets/AbstractPreset';

const settings = window.measurementSelectorVariables;
const client = new Client( settings.restUrl );

const asymmetricShapes = [
	'right-angled-triangle',
	'right-angled-triangle-mirrored',
	'arrow',
	'sloped-rectangle',
	'trapezoid',
	'text',
	'dxf',
];

const fallbackModel = new makerjs.models.Rectangle( 1, 1 );
const enabledShapes = settings.enabledShapes;
const shape = settings.shape;
const dimensions = {};

let modifiers  = {
	'desired_dimension': 'height',
	'use_individual_corners': 'rounded-rectangle' in settings.pricingGroup.shape_settings && settings.pricingGroup.shape_settings[ 'rounded-rectangle' ][ 'use_individual_corners' ],
};

enabledShapes.forEach( ( enabledShape ) => {
	dimensions[ enabledShape ] = settings.shapeSettings.defaultDimensions[ enabledShape ];

	// TODO @Willemijnr bij het rechttrekken van height/length dit ook meenemen aub
	if ( enabledShape === 'rectangle' ) {
		dimensions[ enabledShape ].length = settings.shapeSettings.defaultDimensions.rectangle['height'];
		delete dimensions[ enabledShape ].height;
	}
} );

// When no dimensions are passed, PHP defaults to passing an array [] which would break our code.
if ( ! isArray( settings.dimensions ) ) {
	dimensions[ shape ] = settings.dimensions;
}
if ( ! isArray( settings.modifiers ) ) {
	modifiers = settings.modifiers;
}

const retrieveDxfDefinitionDebouncedFn = debounce( async ( dispatch ) => {
	dispatch('retrieveDxfDefinition');
}, 1000, { leading: true, trailing: true } );

export const store = createStore({
	modules: {
		cutout: cutoutState,
		drillHole: drillHoleState,
		config: config,
		fonts: fonts,
		uiState: ui,
		validation: validation,
		shapeState,
		edgeFinish: edgeFinishStore,
		fileUploadState
	},
	state: () => ({
		enabledShapes: enabledShapes,
		measurements: {
			shape,
			dimensions,
			modifiers,
			dxfDefinition: {
				svgPath: '',
			},
			sideSelection: 'topSide',
			sideUserSelected: false,
			forceRefresh: 0,
		},
		ui: {
			isRetrievingProperties: false,
			isRetrievingBulkPricing: false,
			scale: 1,
		},
		previewShapeDimensions: {
			width: 0,
			height: 0,
		},
		model: fallbackModel,
		presetShape: '',
	}),
	mutations: {
		forceRefresh( state ): void {
			state.measurements.forceRefresh = Math.random();
		},
		updateModifier( state, { modifier, value } ): void {
			if ( value === '' ) {
				value = 0;
			}

			if ( typeof value === 'string' ) {
				value = value.replace( ',', '.' );
			}

			state.measurements.modifiers[ modifier ] = value;
		},
		updatePresetShape(state, preset): void {
			state.presetShape = preset;
		},
		updateDimension( state, { dimension, value } ): void {
			const { shape } = state.measurements;

			if ( value === '' ) {
				value = 0;
			}

			if ( typeof value === 'string' ) {
				value = value.replace( ',', '.' );
			}

			state.measurements.dimensions[shape][dimension] = value;
		},
		setDimensionsForShape( state, { shape, dimensions } ): void {
			state.measurements.dimensions[shape] = dimensions;
		},
		updateDxfDefinition( state, dxfDefinition ): void {
			state.measurements.dxfDefinition = dxfDefinition;
		},
		updateIsRetrievingProperties( state, isRetrievingProperties ): void {
			state.ui.isRetrievingProperties = isRetrievingProperties;
		},
		updateIsRetrievingBulkPricing( state, isRetrievingBulkPricing ): void {
			state.ui.isRetrievingBulkPricing = isRetrievingBulkPricing;
		},
		updateSideSelection( state, sideSelection ): void {
			state.measurements.sideUserSelected = true;
			state.measurements.sideSelection = sideSelection;
		},
		updatePreviewShapeDimensions( state, { width, height } ): void {
			state.previewShapeDimensions.width = width;
			state.previewShapeDimensions.height = height;
		},
	},
	getters: {
		enabledShapes: ( state, getters ): string[] => {
			if(getters.getBlockSettings) {
				return getters.getBlockSettings.shapes;
			}
			return state.enabledShapes;
		},
		getPresets: ( state, getters ): AbstractPreset[] => {
			const presetFactory = new PresetFactory();
			return presetFactory.getAll(getters.enabledShapes, getters.isCutoutsEnabled, getters.isDrillHolesEnabled);
		},
		presetShape: ( state ): string => state.presetShape,
		activeSection: ( state ): string => state.ui.activeSection,
		shape: ( state ): string => state.measurements.shape,
		getPreviewShapeWidth: ( state ): number => state.previewShapeDimensions.width,
		getPreviewShapeHeight: ( state ): number => state.previewShapeDimensions.height,
		exportShape: ( state, getters ): string => {
			let exportShape = state.measurements.shape;
			if ( getters.drillOption !== 'none' || getters.cutoutType !== CutoutTypes.none || getters.shape === 'dxf') {
				exportShape = 'customized-shape';
			}

			return exportShape;
		},
		dimensions: ( state, { shape } ): object => state.measurements.dimensions[ shape ],
		modifiers: ( state ): object => state.measurements.modifiers,
		dxfExport: ( state, getters ): string => {
			if (!getters.isShapeComplete) {
				return;
			}
			const completeModel = getters.getExportableModel;

			if ( isEmpty( completeModel ) || isEmpty( completeModel.models ) || getters.isUsingFallback ) {
				return;
			}

			const exportModel = makerjs.cloneObject( completeModel );

			return makerjs.exporter.toDXF( exportModel, {units: 'mm'} );
		},
		getExportableModel( state, getters ): IModel|null {
			const emptyModel = {models: {}} as IModel;
			if(!getters.isShapeComplete) {
				return emptyModel;
			}
			const modelDecorator = getters.getExportableModelDecorator;
			if(!modelDecorator) {
				return emptyModel;
			}
			return modelDecorator.getModel();
		},
		isUsingFallback( state, getters ): boolean {
			return isEqual( getters.shapeModel(), fallbackModel );
		},
		dxfDefinition: ( state ): object => state.measurements.dxfDefinition,
		isRetrievingProperties: ( state ): boolean => state.ui.isRetrievingProperties,
		isRetrievingBulkPricing: ( state ): boolean => state.ui.isRetrievingBulkPricing,
		parsedDimensions( state, { dimensions } ): object {
			return mapValues( dimensions, ( ( dimension ) => {
				return parseFloat( dimension );
			} ) );
		},
		primitives( state, { modifiers, parsedDimensions } ): object {
			return {
				...modifiers,
				...parsedDimensions,
			};
		},
		usePriceFromServer( state, { exportShape } ): boolean {
			return exportShape === 'dxf' || exportShape === 'text' || exportShape == 'customized-shape';
		},
		exportCompositeDxf( state, { exportShape } ): boolean {
			return exportShape === 'text' || exportShape == 'customized-shape' || exportShape === 'dxf';
		},
		isAsymmetricShape: ( state, { shape } ): boolean => {
			return asymmetricShapes.includes( shape );
		},
		sideSelection: ( state ): string => state.measurements.sideSelection,
		sideUserSelected: ( state ): string => state.measurements.sideUserSelected,
		showSideSelection( state, getters ): boolean {
			if(!getters.shape) {
				return false;
			}
			return (getters.isAsymmetricShape && settings.settings.sheetSides !== 'symmetrical') || settings.sideSelectionSettings.showSideSelection;
		},
		sideAppearance: ( state, getters ): string => {
			if ( settings.settings.sheetSides === 'asymmetical' ) {
				return settings.i18n.sideSelection.settings[ getters.sideSelection ].appearance ?? 'gloss';
			}
			if ( settings.settings.hasCustomTexture ) {
				return 'custom_texture';
			}
			return settings.settings.displayTexture ?? 'gloss';
		},
		customTexture: (): object => {
			return settings.settings.customTexture;
		},
		sideLabel: ( state, getters ): string => {
			return settings.i18n.sideSelection.settings[ getters.sideSelection ].label;
		},
		sideColor: ( state, getters ): string => {
			if ( settings.settings.sheetSides === 'asymmetical' ) {
				return settings.i18n.sideSelection.settings[ getters.sideSelection ].color || 'white';
			}

			return settings.settings[ 'displayColor' ] ?? 'white';
		},
		forceRefresh: ( state ): number => state.measurements.forceRefresh,
		dimensionPreview: ( state, getters ): string => {
			if ( ! isUndefined( getters[ `${getters.shape}/dimensionPreview` ] ) ) {
				return getters[ `${getters.shape}/dimensionPreview` ];
			}

			if ( !getters.dimensions ) {
				return '';
			}

			const shape = getters.getShapeInstance;

			// Always show height x width in dimensions preview
			const width = shape.getWidth() || 0;
			const height = shape.getHeight() || 0;
			const values = [height, width].map( ( length: Length ) => {
				const value = length.in_cm();
				return formatToLocaleString(value) + 'cm';
			});

			return values.join( ' × ' );
		},
		getMinimum: ( state, getters ): Function => {
			return ( dimension: string ): number => {
				if ( dimension === 'number_of_points' ) {
					return 2;
				}

				if ( dimension === 'side_amount' ) {
					return 3;
				}

				return getters.getMinimalSize;
			};
		},
		getShapeInstance: ( state, getters ): AbstractShape => {
			return getters.baseShapeInstance;
		},
		measurements: ( state ): object => state.measurements,
		getFallbackModel(): Function {
			return (): IModel => {
				return fallbackModel;
			};
		},
		model: ( state, getters ): IModel => {
			const shapeInstance = getters.baseShapeInstance;

			if ( ! shapeInstance ) {
				return getters.getFallbackModel();
			}

			return shapeInstance.render();
		},
		shapeModel( state, getters ): Function {
			return (): IModel => {
				const { shape, getFallbackModel, model } = getters;

				if ( ! getters.isShapeComplete || ! model ) {
					return getFallbackModel();
				}

				return model;
			};
		},
		getBaseShapeModelDecorator( state, getters ): InterfaceModelDecorator {
			return new ShapeModelDecorator(null, getters.baseShapeInstance);
		},
		getExportableModelDecorator(state, getters): InterfaceModelDecorator {
			let modelDecorator = getters.getBaseShapeModelDecorator;

			const baseShapeModel = modelDecorator.getModel();

			// Make sure the base shape is available
			if(isEmpty(baseShapeModel.models)) {
				return;
			}

			modelDecorator = new GeneratedDrillHoleDecorator(modelDecorator, getters.getGeneratedDrillHoleInstance);
			modelDecorator = new CustomDrillHoleDecorator(modelDecorator, getters.getCustomDrillHoles);

			modelDecorator = new CutoutModelDecorator(modelDecorator, getters.cutouts);

			modelDecorator = new ShapeModelCombineDecorator(modelDecorator);

			if(getters.shouldApplyFillet) {
				modelDecorator = new FilletModelDecorator(modelDecorator, getters.filletSize);
			}
			return modelDecorator;
		},
		getPreviewModel(state, getters): String {
			const { baseShapeInstance, currentStepName, hasValidatorErrors } = getters;
			let modelDecorator = getters.getExportableModelDecorator;

			// If there are validation errors, don't show the model
			if(hasValidatorErrors) return '';

			if(!modelDecorator) {
				return '';
			}

			const baseModel = modelDecorator.getModel();

			// Calculate the ratio of the base shape model
			const measure = makerjs.measure.modelExtents(baseModel);
			if(!measure) {
				return '';
			}

			let ratio = Math.max(measure.width / getters.getPreviewShapeWidth, measure.height / getters.getPreviewShapeHeight);
			ratio = ratio === Infinity ? 0.6 : ratio;

			modelDecorator = new DimensionLineDecorator(modelDecorator, baseShapeInstance, ratio, getters.focusedElement, true);
			modelDecorator = new GuideLineDecorator(modelDecorator, baseShapeInstance, getters.focusedElement);

			if(currentStepName === StepTypes.drillholes) {
				modelDecorator = new DrillHoleOffsetLineDecorator(modelDecorator, getters.getGeneratedDrillHoleInstance, getters.focusedElement);
				modelDecorator = new CurrentGeneratedDrillHoleDecorator(modelDecorator, getters.getGeneratedDrillHoleInstance, getters.focusedElement);
			}

			if(currentStepName === StepTypes.drillholes && getters.getCustomDrillHoles.length > 0) {
				modelDecorator = new CurrentCustomDrillHoleDecorator(modelDecorator, getters.getCustomDrillHoles[getters.currentCustomDrillhole], getters.currentCustomDrillhole, getters.focusedElement);
			}

			if(currentStepName === StepTypes.cutouts && getters.cutouts.length > 0) {
				const baseShapeModelDecorator = getters.getBaseShapeModelDecorator;
				const baseShapeModel = baseShapeModelDecorator.getModel();
				modelDecorator = new currentCutoutModelDecorator(modelDecorator, getters.cutouts[getters.currentCutoutIndex], getters.focusedElement, ratio, getters.shouldApplyFillet, getters.filletSize, baseShapeModel);
			}

			const completeModel = modelDecorator.getModel();

			makerjs.model.zero(completeModel);
			makerjs.model.originate(completeModel);

			// Create an invisible rectangle around the model to add an extra padding
			// for the preview and the dimensions lines
			const measureComplete = makerjs.measure.modelExtents(completeModel);
			const padding = 40;
			const paddingModel = new makerjs.models.Rectangle(
				measureComplete.width + (padding * ratio),
				measureComplete.height + (padding * ratio)
			);

			makerjs.model.moveRelative(paddingModel, [
				(padding / -2) * ratio,
				(padding / -2) * ratio,
			]);

			// @ts-expect-error paddingModel is an instance of IModel
			paddingModel.layer = 'hidden-rectangle';
			makerjs.model.addModel(completeModel, paddingModel, 'hidden-rectangle');

			let svgString = makerjs.exporter.toSVG(completeModel, {
				useSvgPathOnly: true, // Every model with a layer defined has their own path inside the svg
				units: 'mm',
				stroke: '#0074b5',
			});

			svgString = addCaptionBackgrounds(svgString, ratio);
			svgString = getTexturedSVG(svgString, getters.sideColor, getters.sideAppearance);
			svgString = dottedGuideLines(svgString);
			svgString = addStyleToSVG(svgString);

			return reorderSVG(svgString);

		},
		showSubtractLayers( state, getters ): boolean {
			return getters.currentStepName !== StepTypes.drillholes;
		},
		dimensionLabel(): ( dimension: string ) => string {
			return ( dimension: string ): string => {
				return window.measurementSelectorVariables.i18n.dimensionNames[dimension];
			};
		},
		mapLabel( state, getters ): (maximums: []) => string {
			return ( maximums ) => {
				return mapValues( maximums, ( maximum: Length, key: string ) => {
					if ( maximum instanceof Length ) {
						return sprintf( settings.i18n.maximum, renderMaxLength( maximum.in_cm() ), 'cm' );
					}

					if ( key === 'number_of_points' || key === 'side_amount' ) {
						return sprintf( settings.i18n.maximum, maximum, '' );
					}

					return `${maximum}`;
				} );
			};
		},

		// Get extra instructions for dimension when the dimension input is focused
		getInstructionsForDimension( state, getters ): (dimension: string, basedOnDimension: string, isCutout: boolean) => {} {
			return ( dimension: string, basedOnDimension: string, isCutout = false ): {} => {
				if(!getters.focusedElement) return '';
				if(!getters.focusedElement.includes(`-${dimension}`)) return '';

				const i18n = window.measurementSelectorVariables.i18n.validators.dimensions;
				const dimensionLabel = getters.dimensionLabel(dimension).toLowerCase();
				const basedOnDimensionLabel = getters.dimensionLabel(basedOnDimension).toLowerCase();

				if(isCutout) {
					const configuration = getters.getConfiguration(getters.cutout.instance.shape);
					const minimums = configuration.get_minimum_dimensions(1, getters.cutoutDimensions);
					const maximums = configuration.get_maximum_variable_dimensions(getters.cutoutDimensions);
					if(!minimums[dimension]) {
						return '';
					}

					if(maximums[dimension]) {
						return sprintf(i18n.basedOnValueBetween, basedOnDimensionLabel, getters.cutoutDimensions[basedOnDimension], dimensionLabel, minimums[dimension]?.in_cm(), maximums[dimension]?.in_cm(), 'cm');
					}

					return sprintf(i18n.basedOnValueMinimum, basedOnDimensionLabel, getters.cutoutDimensions[basedOnDimension], dimensionLabel, minimums[dimension]?.in_cm(), "cm");
				}

				const configuration = getters.getConfiguration( getters.shape );
				const minimums = configuration.get_minimum_dimensions(getters.getMinimalSize, getters.dimensions);
				const maximums = {
					...getters.maximums,
					...Object.fromEntries(

						// Respects the maximums set in the product settings
						Object.entries(configuration.get_maximum_variable_dimensions(getters.dimensions))
							.map(([key, value]: [string, Length]) => {
								const currentMax = getters.maximums[key];
								return [
									key,
									currentMax !== undefined && currentMax.in_mm() < value.in_mm() ? currentMax : value
								];
							})
					),
				};

				// Gets instructions string with correct dimmensions
				return sprintf(i18n.basedOnValueBetween, basedOnDimensionLabel, getters.dimensions[basedOnDimension], dimensionLabel, minimums[dimension]?.in_cm(), maximums[dimension]?.in_cm(), 'cm');
			};
		},
		getMaxLabelForDimension( state, getters ): (dimension: string) => string {
			return ( dimension: string): string => {
				const actualLabel = getters.getShapeInstance.getDimensionLabel(dimension);
				const maxLabel = getters.mapLabel(getters.maximums);
				return maxLabel[actualLabel] ?? '';
			};
		},
		hasAllMeasurements( state, getters ): boolean {
			const currentMeasurements = state.measurements.dimensions[ getters.shape ];

			// DXF's should be always considered valid.
			if ( getters.shape === 'dxf' ) {
				return true;
			}

			if ( isEmpty( currentMeasurements ) ) {
				return false;
			}

			const requiredMeasurements = ( new ShapeConfigurationFactory() ).get_configuration( getters.shape ).get_dimension_keys();

			return isEmpty( requiredMeasurements.filter( ( measurement ) => {
				const current = currentMeasurements[ measurement ];
				return ! has( currentMeasurements, measurement ) || ( ! current && current !== 0 && current !== '0' );
			} ) );
		},
		hasAllModifiers( state, getters ): boolean {
			const currentModifiers = state.measurements.modifiers;
			const requiredModifiers = ( new ShapeConfigurationFactory() ).get_configuration( getters.shape ).get_modifier_keys();

			if ( isEmpty( requiredModifiers ) ) {
				return true;
			}

			return isEmpty( requiredModifiers.filter( ( modifier ) => {
				const current = currentModifiers[ modifier ];
				return ! has( currentModifiers, modifier ) || ( ! current && current !== 0 && current !== '0' );
			} ) );
		},
		isCenteredShape( state, getters ): boolean {
			return [
				'circle',
				'heart',
				'hexagon',
				'octagon',
				'polygon',
				'star',
				'ellipse',
				'quarter-circle',
				'kite',
			].includes( getters.shape );
		},
		shapeObject( state, getters ): AbstractShape|NullShape {
			const { shape, modifiers } = state.measurements;

			return getters.getShapeObject( shape, getters.parsedDimensions, modifiers );
		},
		getShapeObject(): Function {
			return ( shape: string, dimensions: object, modifiers = {} ): AbstractShape|NullShape => {
				if ( isEmpty( dimensions ) ) {
					return new NullShape();
				}

				return ( new ShapeFactory() ).create_shape_from_primitives(
					shape,
					{
						...convertUnits( dimensions ),
						...modifiers
					}
				);
			};
		},
		getArea( state, getters ): Function {
			return ( shape: string, dimensions: object, modifiers = {} ): number => {
				const instance = getters.getShapeObject( shape, dimensions, modifiers );

				if ( instance === NullShape ) {
					return 0;
				}

				return instance.get_area().area ?? 0;
			};
		},
		canUseExactCenter( state, getters ): boolean {
			const center = getters.getCenter( false, 'cm' );

			return countDecimals( center.x ) <= 1 && countDecimals( center.y ) <= 1;
		},
		hasProblematicCorners( state, getters ): boolean {
			if(!getters.isShapeComplete) {
				return false;
			}
			const model = getters.getExportableModel;
			let problemPoints = 0;

			const fillet = getters.filletSize;

			// Check model for remaining sharp corners
			const chains = makerjs.model.findChains( model as IModel, { contain: { alternateDirection: true }, pointMatchingDistance: getters.filletSize.in_mm() } ) as makerjs.IChain[];

			if(!Array.isArray(chains)) {
				return false;
			}

			for(let i = 0; i < chains.length; i++) {
				const chain = chains[i];
				if(chain.links.length > 1) {

					// Foreach item in the chain link
					for(let j = 0; j < chain.links.length; j++) {

						// Set link and next link, and set next link to first link if at the end of the chain
						const link = chain.links[j];
						const nextLink = j + 1 < chain.links.length ? chain.links[j + 1] : chain.links[0];

						// Check if the angle between the two links is less than 90 degrees
						const angle = makerjs.angle.ofChainLinkJoint(link, nextLink);

						const midpoint = link.reversed ? link.endPoints[0] : link.endPoints[1];
						const startpoint = link.reversed ? link.endPoints[1] : link.endPoints[0];

						if( angle >= 45 ) {
							problemPoints++;
							if(window.appDebug ?? false) {
								makerjs.model.addModel(model, new makerjs.models.Holes(fillet.in_mm(), [midpoint]), 'debug_fillet_' + i + '.' + j + 'angle_' + angle);
								makerjs.model.addModel(model, new makerjs.models.Holes(fillet.in_mm()/2, [startpoint, nextLink.endPoints[1]]), 'debug_fillet_' + i + '.' + j + 'angle_' + angle);
							}
						}
					}
				}

				// Check contained chains
				if(typeof chain.contains !== 'undefined' && chain.contains.length > 0) {
					for(let j = 0; j < chain.contains.length; j++) {
						const childChain = chain.contains[j];
						if(childChain.links.length > 1) {

							// Foreach item in the chain link
							for(let j = 0; j < childChain.links.length; j++) {

								// Set link and next link, and set next link to first link if at the end of the childChain
								const link = childChain.links[j];
								const nextLink = j + 1 < childChain.links.length ? childChain.links[j + 1] : childChain.links[0];

								// Check if the angle between the two links is less than 90 degrees
								const angle = makerjs.angle.ofChainLinkJoint(link, nextLink);

								const midpoint = link.reversed ? link.endPoints[0] : link.endPoints[1];
								const startpoint = link.reversed ? link.endPoints[1] : link.endPoints[0];

								if( angle >= 45 ) {
									problemPoints++;
									if(window.appDebug ?? false) {
										makerjs.model.addModel(model, new makerjs.models.Holes(fillet.in_mm(), [midpoint]), 'debug_fillet_' + i + '.' + j + 'angle_' + angle);
										makerjs.model.addModel(model, new makerjs.models.Holes(fillet.in_mm()/2, [startpoint, nextLink.endPoints[1]]), 'debug_fillet_' + i + '.' + j + 'angle_' + angle);
									}
								}
							}
						}
					}
				}
			}

			return problemPoints > 0;
		},
		getPricingGroup(): PricingGroup {

			// @ts-expect-error possible wrong types in constructor
			return new PricingGroup( settings.pricingGroup );
		},
	},
	actions: {
		async retrieveDxfDefinitionDebounced( { dispatch } ): Promise<void> {
			retrieveDxfDefinitionDebouncedFn(dispatch);
		},
		async retrieveDxfDefinition( { commit, rootGetters, getters } ): Promise<void> {
			if ( ! rootGetters.isShapeComplete ) {
				commit( 'updateDxfDefinition', { svgPath: '' } );
				commit( 'updateIsRetrievingProperties', false );
				commit( 'updateUsingDefaultDimensions', false );
				return;
			}

			if ( rootGetters.isUsingFallback ) {
				commit( 'updateDxfDefinition', { svgPath: '' } );
				commit( 'updateIsRetrievingProperties', false );
				commit( 'updateUsingDefaultDimensions', false );
				return;
			}

			if ( !rootGetters.usePriceFromServer ) {
				commit( 'updateDxfDefinition', { svgPath: '' } );
				commit( 'updateIsRetrievingProperties', false );
				commit( 'updateUsingDefaultDimensions', false );
				return;
			}
			commit( 'updateIsRetrievingProperties', true );
			const file = new Blob( [ getters.dxfExport ], { type: 'application/dxf' } );

			const data = await client.getProperties( settings.product_id, file, getters.exportShape, rootGetters.activeEdgeFinish );

			commit( 'updateDxfDefinition', data );
			commit( 'updateIsRetrievingProperties', false );
			commit( 'updateUsingDefaultDimensions', false );
		},
		async updatePlateShape( { dispatch }, shape ): Promise<void> {
			await dispatch( 'updateShape', shape );

			await dispatch( 'resetCutouts' );
			await dispatch( 'resetDrillHoles' );
		},
		async  updatePlateDimension( { commit, getters, dispatch }, { dimension, value } ): Promise<void> {
			commit( 'updateDimension', { dimension, value } );
			commit( 'updateUsingDefaultDimensions', false );

			if ( getters.cutout && getters.usesDefaultCutoutDimensions === true ) {
				await dispatch( 'updateCutoutPosition' );
			}
		},
		async updateShapeDimensions( { commit, state, getters }, shape ): Promise<void> {
			if ( shape === 'dxf' || shape === 'rectangle' ) {
				return;
			}

			const instance = getters.baseShapeInstance;

			if ( ! ( 'getDefaultDimensions' in instance ) ) {
				return;
			}

			// Convert the numerical dimensions to strings. Non-numerical values are set to undefined.
			let dimensions = mapValues( instance.getDefaultDimensions(), ( primitive ) => {
				if ( typeof primitive !== 'number' ) {
					return;
				}

				return primitive.toString();
			} );

			// Any value that is considered undefined is removed.
			dimensions = omitBy( dimensions, isUndefined );

			switch ( shape ) {
				case 'star':
					if ( !state.measurements.modifiers.number_of_points ) {
						commit( 'updateModifier', { modifier: 'number_of_points', value: 5 } );
					}

					if ( isEmpty( state.measurements.dimensions[ shape ] ) ) {
						commit( 'setDimensionsForShape', { shape, dimensions } );
					}
					break;
				case 'polygon':
					if ( !state.measurements.modifiers.side_amount ) {
						commit( 'updateModifier', { modifier: 'side_amount', value: 8 } );
					}
					if ( isEmpty( state.measurements.dimensions[ shape ] ) ) {
						commit( 'setDimensionsForShape', { shape, dimensions } );
					}
					break;
				default:
					if ( isEmpty( state.measurements.dimensions[ shape ] ) ) {
						commit( 'setDimensionsForShape', { shape, dimensions } );
					}
					break;
			}

			commit( 'updateUsingDefaultDimensions', false );
		},
		async updateShape( { commit, state, getters, dispatch }, shape ): Promise<void> {
			state.measurements.shape = shape;

			if ( shape === 'text' ) {
				await dispatch( 'resetCutouts' );
				await dispatch( 'resetDrillHoles' );

				await dispatch( 'text/setDefaults', false, { root: true } );
			}

			if( shape === 'dxf' ) {
				await dispatch( 'resetCutouts' );
				await dispatch( 'resetDrillHoles' );
			}

			if ( shape !== null && getters.baseShapeInstance !== null ) {
				await dispatch( 'updateShapeDimensions', shape );
			}

			if ( !getters.isAsymmetricShape ) {
				commit( 'updateSideSelection', 'topSide' );
			}

			commit( 'updateZoom', 1 );
			commit( 'updateRotation', 0 );
			commit( 'updateTranslation', [ 0, 0 ] );

			if ( !shape || 0 === shape.length ) {
				return;
			}

			window?.dataLayer?.push( {
				event: "measurements_selector",
				eventCategory: "Shapes & Sizes: " + document.title,
				eventAction: "Step 2. Choose Shape",
				eventLabel: shape,
			} );
		},
		loadPresetConfig( { commit, dispatch }, preset ): void {
			const config = preset.getConfig();

			commit( 'updatePresetShape', preset.getName() );
			commit( 'updateLastConfiguration', config );
			dispatch( 'updateFromConfig', config );
		}
	},
} );
